import {Injectable} from '@angular/core';
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {Snapshot} from "../models/snpashot.model";
import {LeadDgSearchResults} from "../models/datagrid-search-results/lead.dg-search-results.model";
import {Lead, LeadStatus} from "../models/lead.model";

/**
 * Serwis warstwy danych pełniący rolę wbudowanego modułu CRM.
 * Odpowiada za zarządzanie potencjalnymi klientami (Leads), procesem ich ofertowania,
 * weryfikacją danych rejestrowych (NIP), obsługą kodów promocyjnych
 * oraz finalną konwersją (akceptacją) Leada na pełnoprawnego Klienta B2B.
 */
@Injectable({
  providedIn: 'root'
})
export class LeadService implements DataGridServiceInterface<Observable<LeadDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę potencjalnych klientów dla widoku tabelarycznego działu sprzedaży.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<LeadDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/leads/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new LeadDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Lead(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje Leady na podstawie zaawansowanych kryteriów (np. nazwa firmy, NIP, status w lejku).
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<LeadDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/leads/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new LeadDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new Lead(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje dane potencjalnego klienta w bazie danych (np. po rozmowie telefonicznej).
   */
  updateLead(leadEdited: Lead): Observable<Lead> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/leads', leadEdited).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Dodaje lub aktualizuje notatkę wewnętrzną do profilu Leada.
   */
  updateLeadComment(leadUUID: string, comment: string): Observable<Lead> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/leads/comment', {
      'uuid': leadUUID,
      'comment': comment
    }).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Tworzy nowego potencjalnego klienta w systemie.
   * Zawiera wbudowaną walidację biznesową zabezpieczającą przed duplikacją (sprawdzanie po numerze NIP).
   */
  addLead(lead: Lead): Observable<Lead> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/leads`, lead).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        if (vetineoApiResponse.errors) {
          if (vetineoApiResponse.errors.main === 'NIP_ALREADY_EXIST') {
            return new Lead();
          }
        } else {
          return undefined;
        }
      }
    }));
  }

  /**
   * Przesuwa Leada do kolejnego etapu w lejku sprzedażowym (np. 'NOWY' -> 'W_TRAKCIE_ROZMÓW' -> 'UMOWA_WYSLANA').
   */
  changeStatus(uuid: string, leadStatus: LeadStatus): Observable<Lead> {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/leads/status`, {
      'uuid': uuid,
      'status': leadStatus.toString()
    }).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera historię zmian w profilu Leada.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/leads/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Odrzuca (usuwa) Leada z lejka sprzedażowego, wymagając podania powodu ('description').
   */
  deleteLead(uuid: string, description: string) {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/leads/delete`, {
      'uuid': uuid,
      'description': description
    }).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Metoda wyrażająca akceptację zgody na przekazanie danych kontaktowych przez potencjalnego klienta.
   */
  acceptLead(lead: Lead): Observable<Lead> {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/leads/accept`, lead).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera profil Leada do zasilenia formularza edycji.
   */
  getLeadToUpdated(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/leads/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
         throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły Leada.
   */
  getLeadDetails(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/leads/details/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Zmienia status Leada po podpisaniu aneksu do umowy.
   */
  markLeadAsAnexSigned(uuid: any): Observable<Lead> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/leads/signed/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera treść klauzuli/obowiązku informacyjnego przypisanego do formularza rejestracji z kodem promocyjnym.
   */
  getPromotionalCodeIob(): Observable<string> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/rc/iob').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.value;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Weryfikuje poprawność i ważność podanego kodu promocyjnego/referencyjnego.
   */
  checkPromotionalCode(promotionalCode: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/rc-check/${promotionalCode}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return "OK";
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Tworzy nowego Leada przypisując mu od razu sprawdzony kod promocyjny
   */
  addLeadWithPromotionalCode(lead: Lead) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/registration-code-lead/${lead.promotionalCode}`, lead).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<Lead>(new Lead(), vetineoApiResponse.response);
      } else {
        if (vetineoApiResponse.errors) {
          throw new Error(vetineoApiResponse.errors.main)
        }
      }
    }));
  }

  /**
   * Pobiera ogólny regulamin korzystania z platformy niezbędny do akceptacji na etapie rejestracji Leada.
   */
  getClinicTerms() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/rc/cl-terms').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.value;
      } else {
        return undefined;
      }
    }));
  }
}
